import { useEffect, useState } from "react";
import {
  TECH_API,
  apiFetch,
  apiPost,
  apiPatch,
  Loader,
  Icon,
} from "./apihandler";

export default function PricingConfigurationPage({
  onNext,
  onBack,
  technicianId,
  campId,
  serviceId,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [resourceType, setResourceType] = useState("INTERNAL");
  const [pricingId, setPricingId] = useState(null);

  const [formData, setFormData] = useState({
    pricingModel: "MONTHLY",
    rate: "",
    effectiveFrom: "",
    paymentCycle: "MONTH",
    isActive: true,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const technician = await apiFetch(
          `${TECH_API}/technicians/${technicianId}/`
        );

        setResourceType(technician.resource_type || "INTERNAL");

        const pricingRes = await apiFetch(
          `${TECH_API}/technician-pricing/?technician=${technicianId}&camp=${campId}&service=${serviceId}`
        );

        const existing = pricingRes.results?.[0];
        if (existing) {
          setPricingId(existing.id);
          setFormData({
            pricingModel: existing.pricing_model || "MONTHLY",
            rate: existing.rate || "",
            effectiveFrom:
              existing.effective_from?.split("T")[0] || "",
            paymentCycle: existing.payment_cycle || "MONTH",
            isActive: existing.is_active ?? true,
          });
        }
      } catch (err) {
        console.error("Failed to load pricing", err);
      } finally {
        setLoading(false);
      }
    };

    if (technicianId) fetchData();
  }, [technicianId, campId, serviceId]);

  const handleChange = (field, value) => {
    setFormData((p) => ({ ...p, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.rate) return alert("Rate is required");
    if (!formData.effectiveFrom) return alert("Effective date required");

    setSaving(true);
    try {
      const payload = {
        technician: technicianId,
        camp: campId,
        service: serviceId,
        pricing_model: formData.pricingModel,
        rate: parseFloat(formData.rate),
        effective_from: formData.effectiveFrom,
        is_active: formData.isActive,
      };

      if (pricingId) {
        await apiPatch(
          `${TECH_API}/technician-pricing/${pricingId}/`,
          payload
        );
      } else {
        await apiPost(`${TECH_API}/technician-pricing/`, payload);
      }

      onNext();
    } catch (err) {
      console.error(err);
      alert("Failed to save pricing configuration");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader text="Loading pricing configuration..." />;

  const isExternal = resourceType !== "INTERNAL";

  return (
    <div className="page">
        <div className="ph">
        <Icon n="dollar" /> Pricing configuration
        </div>

        <div className="info-note">
        <Icon n="info" /> Configure the technician payout model for this camp.
        </div>

        <div className="resource-badge">
        <strong>Resource type:</strong> {isExternal ? "External" : "Internal"}
        </div>

        {isExternal && (
        <>
            <div className="section-label">Pricing model</div>
            <div className="radio-group">
            {[
                ["Per case", "PER_CASE"],
                ["Per day", "PER_DAY"],
                ["Weekly", "WEEKLY"],
                ["Per camp", "PER_CAMP"],
            ].map(([label, value]) => (
                <label
                key={value}
                className={`radio-pill ${formData.pricingModel === value ? "active" : ""}`}
                >
                <input
                    type="radio"
                    name="pricingModel"
                    checked={formData.pricingModel === value}
                    onChange={() => handleChange("pricingModel", value)}
                />
                {label}
                </label>
            ))}
            </div>
        </>
        )}

        <div className="form-row">
        <div className="form-group">
            <label>{!isExternal || formData.pricingModel === "MONTHLY" ? "Monthly salary" : "Rate"}</label>
            <input
            type="number"
            value={formData.rate}
            onChange={(e) => handleChange("rate", e.target.value)}
            placeholder={
                formData.pricingModel === "PER_CASE" ? "e.g. 80 per case"
                : formData.pricingModel === "PER_DAY" ? "e.g. 2000 per day"
                : "e.g. 45000"
            }
            />
        </div>
        <div className="form-group">
            <label>Payment cycle</label>
            <select
            value={formData.paymentCycle}
            onChange={(e) => handleChange("paymentCycle", e.target.value)}
            >
            <option value="MONTH">Month</option>
            <option value="WEEK">Week</option>
            <option value="FORTNIGHT">Fortnight</option>
            </select>
        </div>
        </div>

        <div className="form-group">
        <label>Effective from</label>
        <input
            type="date"
            value={formData.effectiveFrom}
            onChange={(e) => handleChange("effectiveFrom", e.target.value)}
            style={{ maxWidth: 240 }}
        />
        </div>

        <div className="divider" />

        <div className="toggle-row">
        <div>
            <div className="toggle-label">Active configuration</div>
            <div className="toggle-sub">Enables this pricing for billing</div>
        </div>
        <label className="toggle">
            <input
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) => handleChange("isActive", e.target.checked)}
            />
            <span className="slider" />
        </label>
        </div>

        <div className="btn-row">
        <button className="btn" onClick={onBack} disabled={saving}>
            ← Back
        </button>
        <button className="btn btn-pri" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : "Save & continue →"}
        </button>
        </div>

        <style jsx>{`
        .ph { font-size: 18px; font-weight: 500; display: flex; align-items: center; gap: 8px; margin-bottom: 1rem; }
        .info-note { background: #EBF4FF; color: #185FA5; border-radius: 8px; padding: 10px 14px; font-size: 13px; display: flex; align-items: center; gap: 8px; margin-bottom: 1.25rem; }
        .resource-badge { background: #f5f5f4; border: 0.5px solid #e5e5e5; border-radius: 8px; padding: 8px 14px; font-size: 13px; color: #6b7280; display: inline-flex; align-items: center; gap: 6px; margin-bottom: 1.5rem; }
        .resource-badge strong { color: #111; font-weight: 500; }
        .section-label { font-size: 11px; font-weight: 500; letter-spacing: .06em; color: #9ca3af; text-transform: uppercase; margin-bottom: .75rem; }
        .radio-group { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 1.5rem; }
        .radio-pill { display: flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: 8px; border: 0.5px solid #d1d5db; background: #fff; font-size: 13px; color: #6b7280; cursor: pointer; }
        .radio-pill.active { border-color: #185FA5; background: #E6F1FB; color: #0C447C; font-weight: 500; }
        .radio-pill input { display: none; }
        .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 1rem; }
        .form-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 1rem; }
        .form-group label { font-size: 13px; font-weight: 500; color: #6b7280; }
        .form-group input, .form-group select { font-size: 14px; padding: 9px 12px; border: 0.5px solid #d1d5db; border-radius: 8px; width: 100%; }
        .divider { height: 0.5px; background: #e5e7eb; margin: 1.25rem 0; }
        .toggle-row { display: flex; align-items: center; justify-content: space-between; padding: 12px 14px; border: 0.5px solid #e5e7eb; border-radius: 8px; margin-bottom: 1.5rem; }
        .toggle-label { font-size: 14px; color: #111; }
        .toggle-sub { font-size: 12px; color: #9ca3af; margin-top: 2px; }
        .toggle { position: relative; width: 40px; height: 22px; }
        .toggle input { display: none; }
        .slider { position: absolute; inset: 0; background: #d1d5db; border-radius: 11px; cursor: pointer; transition: .2s; }
        .slider:before { content: ''; position: absolute; width: 16px; height: 16px; left: 3px; top: 3px; background: #fff; border-radius: 50%; transition: .2s; }
        .toggle input:checked + .slider { background: #185FA5; }
        .toggle input:checked + .slider:before { transform: translateX(18px); }
        .btn-row { display: flex; justify-content: space-between; align-items: center; padding-top: 1rem; border-top: 0.5px solid #e5e7eb; }
        .btn { padding: 9px 22px; border-radius: 8px; border: 0.5px solid #d1d5db; background: #fff; font-size: 14px; cursor: pointer; }
        .btn-pri { background: #185FA5; border-color: #185FA5; color: #fff; font-weight: 500; }
        .btn:disabled { opacity: 0.5; cursor: not-allowed; }
        `}</style>
    </div>
    )};